import React, { useEffect, useState } from 'react'
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  ConfirmButton,
  EmptyBox,
  FormControl,
  Heading,
  Icon,
  Input,
  ReadOnlyCopyInput,
  RichText,
  SimpleTable,
  SimpleTabs,
  Stack,
  Text,
  Textarea,
  Badge
} from '@/components/lib'

export default function InformedConsentFormTab ({
  trial,
  activeConsentDocument,
  consentDocuments,
  selectConsentDocument,
  reloadConsentDocuments
}) {
  const { user, team } = useStore()

  const [editorDocKey, setEditorDocKey] = useState(
    activeConsentDocument?._id || 'no-doc'
  )

  const [consentFormKey, setConsentFormKey] = useState('default')
  const [consentFormName, setConsentFormName] = useState('Consent Form')
  const [email, setEmail] = useState('')
  const [withdrawalEmail, setWithdrawalEmail] = useState('')
  const [postConsentText, setPostConsentText] = useState('')
  const [postSubmissionText, setPostSubmissionText] = useState('')
  const [connectContactDetails, setConnectContactDetails] = useState(false)
  const [verificationMethods, setVerificationMethods] = useState([])
  const [identityVerificationEnabled, setIdentityVerificationEnabled] =
    useState(false)
  const [consentDocument, setConsentDocument] = useState('')
  const [eligibility, setEligibility] = useState('')
  const [conditions, setConditions] = useState([])
  const [savingDocument, setSavingDocument] = useState(false)
  const [deletingDocument, setDeletingDocument] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [error, setError] = useState('')

  const consentFormUrl = activeConsentDocument?.formKey
    ? `${window.location.origin}/consent/${trial?.code}/${encodeURIComponent(activeConsentDocument.formKey)}`
    : ''

  useEffect(() => {
    if (!activeConsentDocument) {
      setConsentFormKey('default')
      setConsentFormName('Consent Form')
      setConsentDocument('')
      setEligibility('')
      setConditions([])
      setEmail('')
      setWithdrawalEmail('')
      setConnectContactDetails(false)
      setVerificationMethods([])
      setIdentityVerificationEnabled(false)
      setPostConsentText('')
      setPostSubmissionText('')
      setEditorDocKey('no-doc')
      setShowEditor(false)
      return
    }

    setConsentFormKey(activeConsentDocument?.formKey || 'default')
    setConsentFormName(activeConsentDocument?.name || 'Consent Form')
    setConsentDocument(activeConsentDocument?.document || '')
    setEligibility(activeConsentDocument?.eligibilityCriteria || '')
    setConditions(activeConsentDocument?.conditions || [])
    setEmail(activeConsentDocument?.notificationEmail || '')
    setWithdrawalEmail(activeConsentDocument?.withdrawalEmail || '')
    setConnectContactDetails(
      activeConsentDocument?.connectContactDetails || false
    )
    setVerificationMethods(activeConsentDocument?.verificationMethods || [])
    setIdentityVerificationEnabled(
      activeConsentDocument?.identityVerificationEnabled || false
    )
    setPostConsentText(activeConsentDocument?.postConsentText || '')
    setPostSubmissionText(activeConsentDocument?.postSubmissionText || '')
    setEditorDocKey(activeConsentDocument._id)
    setShowEditor(true)
  }, [activeConsentDocument])

  function saveInPlace () {
    if (!activeConsentDocument?._id) {
      return setError('No consent document is selected')
    }
    setSavingDocument(true)
    setError('')
    api.trials.updateConsentDocument(
      trial._id,
      activeConsentDocument._id,
      {
        document: consentDocument,
        formKey: consentFormKey,
        name: consentFormName,
        notificationEmail: email || null,
        withdrawalEmail: withdrawalEmail || null,
        connectContactDetails,
        verificationMethods,
        identityVerificationEnabled,
        eligibilityCriteria: eligibility,
        conditions,
        postConsentText,
        postSubmissionText
      },
      (updatedDoc) => {
        setSavingDocument(false)
        utils.toast('success', 'Consent form updated')
        reloadConsentDocuments(updatedDoc._id)
      },
      (err) => {
        setSavingDocument(false)
        setError(err.message)
      }
    )
  }

  function generateUniqueFormKey (baseKey) {
    // Check if baseKey is available
    const existingKeys = new Set(consentDocuments?.map((d) => d.formKey) || [])

    // If baseKey is not taken, use it
    if (!existingKeys.has(baseKey)) {
      return baseKey
    }

    // Try baseKey2, baseKey3, etc.
    let n = 2
    while (true) {
      const candidate = `${baseKey}${n}`
      if (!existingKeys.has(candidate)) {
        return candidate
      }
      n++
    }
  }

  function createDocument () {
    const isFirst = !consentDocuments?.length
    const baseKey = isFirst ? 'default' : 'form'
    const formKey = generateUniqueFormKey(baseKey)
    setSavingDocument(true)
    setError('')
    api.trials.createConsentDocument(
      trial._id,
      {
        document: '<p></p>',
        formKey,
        name: isFirst ? 'Default Consent Form' : 'New Consent Form',
        notificationEmail: null,
        connectContactDetails: false,
        verificationMethods: ['email'],
        identityVerificationEnabled: false,
        eligibilityCriteria: '',
        conditions: [],
        postConsentText: '',
        postSubmissionText: ''
      },
      (newDoc) => {
        setSavingDocument(false)
        utils.toast('success', 'Consent form created')
        reloadConsentDocuments(newDoc._id)
      },
      (err) => {
        setSavingDocument(false)
        setError(err.message)
      }
    )
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h2' size='md'>
          Informed consent forms
        </Heading>
        <Text>
          Complete the sections below to configure the informed consent form and
          its settings.
        </Text>
      </Box>

      <Stack
        direction={{ base: 'column', md: 'row' }}
        gap={5}
        alignItems='flex-start'
      >
        <Box
          w={{ base: '100%', md: '320px' }}
          maxW={{ base: '100%', md: '320px' }}
        >
          <Card gap={4} overflow='hidden'>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Heading as='h3' size='sm'>
                Consent forms
              </Heading>
              {utils.canWriteTrial(user, trial) &&
                (consentDocuments?.length === 0
                  ? (
                    <Button
                      size='xs'
                      colorPalette='primary'
                      isLoading={savingDocument}
                      onClick={createDocument}
                    >
                      <Icon as={FaPlus} mr={2} /> Create new
                    </Button>
                    )
                  : (
                    <ConfirmButton
                      closeOnBlur
                      trigger={
                        <Button
                          size='xs'
                          colorPalette='primary'
                          isLoading={savingDocument}
                        >
                          <Icon as={FaPlus} mr={2} /> Create new
                        </Button>
                    }
                      header={
                        <Box>
                          <Text>Create a new consent form?</Text>
                        </Box>
                    }
                      footer={
                        <Box>
                          <Text>
                            This will create a blank consent form and select it
                            for editing. You can rename it in the Settings tab.
                          </Text>
                        </Box>
                    }
                      onConfirm={createDocument}
                      actionColor='primary'
                    />
                    ))}
            </Stack>

            <Box overflowY='auto' maxH='60vh' pr={1}>
              {consentDocuments?.length === 0
                ? (
                  <Text color='fg.muted' fontSize='sm'>
                    No consent forms yet.
                  </Text>
                  )
                : (
                  <Stack gap={2}>
                    {consentDocuments.map((d) => (
                      <Box
                        key={d._id}
                        justifyContent='space-between'
                        onClick={() => selectConsentDocument(d)}
                        cursor='pointer'
                        bg={
                        activeConsentDocument?._id === d._id
                          ? 'bg.muted'
                          : 'bg.surface'
                      }
                        color={
                        activeConsentDocument?._id === d._id
                          ? 'fg.default'
                          : 'fg.muted'
                      }
                        p={3}
                        rounded='md'
                      >
                        <Stack gap={1} alignItems='start'>
                          <Text fontWeight='bold' fontSize='sm'>
                            {d.name || 'Consent Form'}
                          </Text>
                          <Stack direction='row' gap={1} alignItems='center'>
                            {d.formKey && (
                              <Badge size='sm' colorPalette='blue'>
                                {d.formKey}
                              </Badge>
                            )}
                            {d.consentCount > 0 && (
                              <Badge size='sm' colorPalette='primary'>
                                {d.consentCount ?? 0} consents
                              </Badge>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                  )}
            </Box>
          </Card>
        </Box>

        <Box flex='1' minW={0}>
          {!activeConsentDocument
            ? (
              <EmptyBox
                icon='signature'
                title='No consent form selected'
                description='Select a consent form from the sidebar, or create a new one to start editing.'
              />
              )
            : (
              <Stack gap={4}>
                <Card gap={2}>
                  <Heading as='h3' size='sm'>
                    Consent form URL
                  </Heading>
                  <ReadOnlyCopyInput value={consentFormUrl} placeholder='—' />
                </Card>

                <SimpleTabs
                  wrapped
                  tabs={[
                    utils.canReadTrial(user, trial) && {
                      title: 'Participant Information Sheet',
                      content: (
                        <Stack gap={5}>
                          <Box>
                            <Heading as='h3' size='sm'>
                              Participant Information Sheet (PIS)
                            </Heading>
                            <Text mb={3}>
                              In the box below, write the text that you'd like
                              participants to read and consent to before joining
                              this trial.
                            </Text>
                          </Box>
                          {showEditor && (
                            <RichText
                              key={`pis-${editorDocKey}`}
                              value={consentDocument}
                              onChange={(e) => setConsentDocument(e)}
                              forType='trial'
                              forId={trial?._id}
                              allowImages
                            />
                          )}
                        </Stack>
                      )
                    },
                    utils.canReadTrial(user, trial) && {
                      title: 'Eligibility',
                      content: (
                        <Stack gap={5}>
                          <Box>
                            <Heading as='h3' size='sm'>
                              Eligibility
                            </Heading>
                            <Text mb={3}>
                              In the box below, write any eligibility criteria
                              that participants must comply with in order to take
                              part in this trial.
                            </Text>
                          </Box>
                          {showEditor && (
                            <RichText
                              key={`eligibility-${editorDocKey}`}
                              value={eligibility}
                              onChange={(e) => setEligibility(e)}
                              forType='trial'
                              forId={trial?._id}
                            />
                          )}
                        </Stack>
                      )
                    },
                    utils.canReadTrial(user, trial) && {
                      title: 'Conditions',
                      content: (
                        <Stack gap={5}>
                          <Box>
                            <Heading as='h3' size='sm'>
                              Conditions
                            </Heading>
                            <Text>
                              Use this section to add any conditions that
                              participants must agree to in order to join the
                              trial.
                            </Text>
                          </Box>

                          {conditions?.length > 0 && (
                            <SimpleTable
                              headers={[
                                '',
                                'Title',
                                'Description',
                                'Optional',
                                'Actions'
                              ]}
                              rows={conditions?.map((condition, i) => [
                                <Box key={condition.id}>
                                  <Stack>
                                    {i > 0 && (
                                      <Button
                                        size='xs'
                                        variant='ghost'
                                        disabled={i === 0}
                                        onClick={() => {
                                          const newConditions = Object.assign(
                                            [],
                                            conditions
                                          )
                                          const [movedCondition] =
                                          newConditions.splice(i, 1)
                                          newConditions.splice(
                                            i - 1,
                                            0,
                                            movedCondition
                                          )
                                          setConditions(newConditions)
                                        }}
                                      >
                                        <Icon as={FaArrowUp} />
                                      </Button>
                                    )}
                                    {i < conditions.length - 1 && (
                                      <Button
                                        size='xs'
                                        variant='ghost'
                                        disabled={i === conditions.length - 1}
                                        onClick={() => {
                                          const newConditions = Object.assign(
                                            [],
                                            conditions
                                          )
                                          const [movedCondition] =
                                          newConditions.splice(i, 1)
                                          newConditions.splice(
                                            i + 1,
                                            0,
                                            movedCondition
                                          )
                                          setConditions(newConditions)
                                        }}
                                      >
                                        <Icon as={FaArrowDown} />
                                      </Button>
                                    )}
                                  </Stack>
                                </Box>,
                                <Input
                                  key={condition.id}
                                  placeholder='Condition title'
                                  value={condition.title}
                                  onChange={(e) => {
                                    const newConditions = Object.assign(
                                      [],
                                      conditions
                                    )
                                    conditions[i].title = e.target.value
                                    setConditions(newConditions)
                                  }}
                                />,
                                <Textarea
                                  key={condition.id}
                                  placeholder='Condition description'
                                  value={condition.description}
                                  onChange={(e) => {
                                    const newConditions = Object.assign(
                                      [],
                                      conditions
                                    )
                                    conditions[i].description = e.target.value
                                    setConditions(newConditions)
                                  }}
                                />,
                                <Box key={`${condition.id}-optional`}>
                                  <Checkbox
                                    size='sm'
                                    isChecked={condition.optional === true}
                                    onChange={(e) => {
                                      const newConditions = Object.assign(
                                        [],
                                        conditions
                                      )
                                      newConditions[i] = Object.assign(
                                        {},
                                        newConditions[i],
                                        { optional: e.checked }
                                      )
                                      setConditions(newConditions)
                                    }}
                                  >
                                    Optional
                                  </Checkbox>
                                </Box>,
                                <Box key={condition.id}>
                                  <ConfirmButton
                                    closeOnBlur
                                    trigger={
                                      <Button variant='ghost'>
                                        <Icon as={FaTrash} />
                                      </Button>
                                  }
                                    header='Really delete this condition?'
                                    footer='You will be able to re-add it by adding a new condition.'
                                    onConfirm={() => {
                                      const newConditions = Object.assign(
                                        [],
                                        conditions
                                      ).filter((c) => c.id !== condition.id)
                                      setConditions(newConditions)
                                    }}
                                    actionColor='red'
                                  />
                                </Box>
                              ])}
                            />
                          )}

                          <Button
                            colorPalette='primary'
                            variant='outline'
                            onClick={() => {
                              const newConditions = Object.assign([], conditions)
                              newConditions.push({
                                id: utils.uuidv4(),
                                title: '',
                                description: '',
                                optional: false
                              })
                              setConditions(newConditions)
                            }}
                          >
                            <Icon as={FaPlus} mr={2} /> Add a condition
                          </Button>
                        </Stack>
                      )
                    },
                    utils.canReadTrial(user, trial) && {
                      title: 'Verification',
                      content: (
                        <Stack gap={5}>
                          <Stack bg='bg.subtle' p={3} rounded='md'>
                            <Box>
                              <Heading as='h3' size='md'>
                                Verification methods
                              </Heading>
                              <Text fontSize='sm'>
                                Select how you'd like participants to verify their
                                consent. You must select at least one option.
                              </Text>
                            </Box>
                            {[
                              {
                                label: 'Text message (SMS)',
                                value: 'phone',
                                description:
                                'Participants can verify using their phone number.'
                              },
                              {
                                label: 'WhatsApp',
                                value: 'whatsapp',
                                description:
                                'Participants can verify using WhatsApp.'
                              },
                              {
                                label: 'Email',
                                value: 'email',
                                description:
                                'Participants can verify using their email address.'
                              }
                            ].map((method) => {
                              if (method.value === 'phone') {
                                if (!utils.hasFeature(team, 'sms')) return null
                              }
                              if (method.value === 'whatsapp') {
                                if (!utils.hasFeature(team, 'whatsapp')) {
                                  return null
                                }
                              }
                              return (
                                <FormControl key={method.value}>
                                  <Checkbox
                                    isChecked={verificationMethods.includes(
                                      method.value
                                    )}
                                    onChange={(e) => {
                                      const newMethods = Object.assign(
                                        [],
                                        verificationMethods
                                      )
                                      if (e.checked) {
                                        newMethods.push(method.value)
                                      } else {
                                        const index = newMethods.indexOf(
                                          method.value
                                        )
                                        if (index > -1) {
                                          newMethods.splice(index, 1)
                                        }
                                      }
                                      setVerificationMethods(newMethods)
                                    }}
                                  >
                                    <Text fontWeight='bold'>{method.label}</Text>
                                    <Text fontSize='sm'>
                                      {method.description}
                                    </Text>
                                  </Checkbox>
                                </FormControl>
                              )
                            })}
                          </Stack>

                          <Stack bg='bg.subtle' p={3} rounded='md'>
                            <Box>
                              <Heading as='h3' size='md'>
                                Contact detail connection
                              </Heading>
                              <Text fontSize='sm'>
                                If you enable this, participant contact details
                                provided through eConsent will be automatically
                                associated with the participant when they join the
                                trial. This allows their details to be used for
                                ongoing contact and notifications.
                              </Text>
                            </Box>
                            <FormControl>
                              <Checkbox
                                isChecked={connectContactDetails}
                                onChange={(e) =>
                                  setConnectContactDetails(e.checked)}
                              >
                                Connect contact details
                              </Checkbox>
                            </FormControl>
                          </Stack>

                          <Stack bg='bg.subtle' p={3} rounded='md' gap={5}>
                            <Box>
                              <Heading as='h3' size='md'>
                                Identity verification
                              </Heading>
                              <Text fontSize='sm'>
                                Participants will be asked to provide their
                                details and match them against official ID
                                documents to verify their identity with our KYC
                                provider. This can help prevent participant fraud.
                              </Text>
                            </Box>
                            {utils.hasFeature(team, 'econsentKyc')
                              ? (
                                <>
                                  <FormControl>
                                    <Checkbox
                                      isChecked={identityVerificationEnabled}
                                      onChange={(e) =>
                                        setIdentityVerificationEnabled(e.checked)}
                                    >
                                      Enable identity verification during the
                                      eConsent process
                                    </Checkbox>
                                  </FormControl>
                                  {identityVerificationEnabled && (
                                    <Alert
                                      status='info'
                                      title='Identity verification billing'
                                    >
                                      <Text fontSize='sm'>
                                        Completed identity verifications are billed
                                        to the trial wallet.
                                      </Text>
                                    </Alert>
                                  )}
                                </>
                                )
                              : (
                                <Alert
                                  status='info'
                                  title='Identity verification is not available'
                                >
                                  <Text>
                                    Identity verification is not available for your
                                    team. Please contact support to enable it.
                                  </Text>
                                </Alert>
                                )}
                          </Stack>
                        </Stack>
                      )
                    },
                    utils.canReadTrial(user, trial) && {
                      title: 'Settings',
                      content: (
                        <Stack gap={5}>
                          <Stack
                            direction={{ base: 'column', md: 'row' }}
                            gap={3}
                          >
                            <FormControl
                              label='Consent form key'
                              helperText='Identifier and for determining the consent form URL (e.g. "parent", "teen").'
                            >
                              <Input
                                value={consentFormKey}
                                onChange={(e) =>
                                  setConsentFormKey(e.target.value)}
                                placeholder='default'
                              />
                            </FormControl>
                            <FormControl
                              label='Consent form name'
                              helperText='Human-friendly label.'
                            >
                              <Input
                                value={consentFormName}
                                onChange={(e) =>
                                  setConsentFormName(e.target.value)}
                                placeholder='Consent Form'
                              />
                            </FormControl>
                          </Stack>

                          <FormControl
                            label='Notification email'
                            helperText='This email address will receive a copy of all signed consent forms.'
                          >
                            <Input
                              id='email'
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder='jane@example.com'
                            />
                          </FormControl>

                          <FormControl
                            label='Withdrawal contact email'
                            helperText='If set, participants will be directed to contact this address in the case that they wish to withdraw consent.'
                          >
                            <Input
                              id='withdrawalEmail'
                              value={withdrawalEmail}
                              onChange={(e) => setWithdrawalEmail(e.target.value)}
                              placeholder='withdrawal@example.com'
                            />
                          </FormControl>

                          <FormControl
                            label='Post-consent on-screen text'
                            helperText='This text will be shown to participants once they have given their consent. It can include instructions for next steps.'
                          >
                            {showEditor && (
                              <Box w='100%'>
                                <RichText
                                  key={`postSubmissionText-${editorDocKey}`}
                                  value={postSubmissionText ?? ''}
                                  onChange={(e) => setPostSubmissionText(e)}
                                  forType='trial'
                                  forId={trial?._id}
                                />
                              </Box>
                            )}
                          </FormControl>

                          <FormControl
                            label='Post-consent notification'
                            helperText={
                              <Box>
                                <Text>
                                  This text will be sent to the consenting
                                  participant via email, SMS, or WhatsApp once
                                  they have given their consent.
                                </Text>
                                <Text
                                  color={
                                  postConsentText?.length > 400 ? 'red' : null
                                }
                                >
                                  {postConsentText?.length || 0}/400 characters
                                </Text>
                              </Box>
                          }
                          >
                            <Textarea
                              id='info'
                              value={postConsentText}
                              onChange={(e) => setPostConsentText(e.target.value)}
                              placeholder='Thank you for providing your eConsent. The next steps are...'
                            />
                          </FormControl>

                          {utils.canWriteTrial(user, trial) && (
                            <Box
                              pt={6}
                              mt={2}
                              borderTopWidth='1px'
                              borderColor='border.subtle'
                            >
                              <ConfirmButton
                                closeOnBlur
                                trigger={
                                  <Button
                                    colorPalette='red'
                                    variant='outline'
                                    isLoading={deletingDocument}
                                  >
                                    <Icon as={FaTrash} mr={2} />
                                    Delete this consent document
                                  </Button>
                              }
                                header={
                                  <Box>
                                    <Text>Delete this consent document?</Text>
                                    {(activeConsentDocument?.consentCount || 0) >
                                    0 && (
                                      <Alert
                                        mt={3}
                                        status='warning'
                                        size='sm'
                                        variant='left-accent'
                                      >
                                        This document has{' '}
                                        {activeConsentDocument?.consentCount}{' '}
                                        signed consents, which will become
                                        "orphaned".
                                      </Alert>
                                    )}
                                  </Box>
                              }
                                footer={
                                  <Box>
                                    <Text>
                                      If this consent was used already, deletion
                                      will also remove linked participant
                                      verification records.
                                    </Text>
                                  </Box>
                              }
                                onConfirm={() => {
                                  const d = activeConsentDocument
                                  setDeletingDocument(true)
                                  setError('')
                                  api.trials.deleteConsentDocument(
                                    trial._id,
                                    d._id,
                                    null,
                                    () => {
                                      setDeletingDocument(false)
                                      utils.toast(
                                        'success',
                                        'Consent document deleted'
                                      )
                                      reloadConsentDocuments()
                                    },
                                    (err) => {
                                      setDeletingDocument(false)
                                      setError(err.message)
                                    }
                                  )
                                }}
                                actionColor='red'
                              />
                            </Box>
                          )}
                        </Stack>
                      )
                    }
                  ]}
                />
              </Stack>
              )}
        </Box>
      </Stack>

      {utils.canWriteTrial(user, trial) && activeConsentDocument?._id && (
        <Box
          mt={10}
          ml={10}
          mr={10}
          position='sticky'
          style={{ bottom: 10 }}
          boxShadow='lg'
          p={3}
          rounded='md'
          bg='bg.subtle'
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            {error
              ? (
                <Alert
                  status='error'
                  title='There was a problem saving your trial details'
                >
                  {error}. Please fix these issues and try again.
                </Alert>
                )
              : (
                <Box />
                )}

            <Stack direction='row' gap={2} alignItems='center'>
              {(activeConsentDocument?.consentCount || 0) > 0
                ? (
                  <ConfirmButton
                    closeOnBlur
                    trigger={
                      <Button colorPalette='primary' isLoading={savingDocument}>
                        Save consent form
                      </Button>
                  }
                    header={
                      <Stack>
                        <Text>Really change this consent form?</Text>
                        <Alert status='warning' size='sm' variant='left-accent'>
                          This consent form already has{' '}
                          {activeConsentDocument?.consentCount} consents made
                          against it.
                        </Alert>
                      </Stack>
                  }
                    footer={
                      <Box>
                        <Text>
                          This will edit the existing consent form in place.
                        </Text>
                      </Box>
                  }
                    onConfirm={saveInPlace}
                    actionColor='primary'
                  />
                  )
                : (
                  <Button
                    colorPalette='primary'
                    isLoading={savingDocument}
                    onClick={saveInPlace}
                  >
                    Save consent form
                  </Button>
                  )}
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  )
}
